import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge, RelationPicker } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { unwrapLookupItems, toInt } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/documentseries';

// Belge Türü: 0=Sipariş, 1=İrsaliye, 2=Fatura (DocumentTemplate.DocType / ResolveData docType ile AYNI)
const DOCUMENT_TYPE_OPTIONS = [
  { value: 0, label: 'Sipariş' },
  { value: 1, label: 'İrsaliye' },
  { value: 2, label: 'Fatura' },
];
// Belge Tipi: 0=Satış, 1=Alış, 2=İade, 3=Hizmet (Invoice.Type semantiğiyle uyumlu)
const DOCUMENT_TIP_OPTIONS = [
  { value: 0, label: 'Satış' },
  { value: 1, label: 'Alış' },
  { value: 2, label: 'İade' },
  { value: 3, label: 'Hizmet' },
];
const ACTIVE_OPTIONS = [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }];

const currentYear = new Date().getFullYear();

const EMPTY_FORM = {
  distributorId: '', year: String(currentYear), documentType: 0, documentTip: 0,
  seriesNo: '', designId: '', isActive: 'true', lastNumber: '0',
};

// Matbu No formatı: SeriesNo + Yıl + 8 haneli sıra. Önizleme (sıradaki) için.
function formatMatbuNo(seriesNo, year, number) {
  const padded = String(Math.max(0, Number(number) || 0)).padStart(8, '0');
  return `${(seriesNo || '').trim()}${year}${padded}`;
}

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}

function rowToForm(row) {
  return {
    distributorId: row.distributorId || '',
    year: row.year != null ? String(row.year) : String(currentYear),
    documentType: row.documentType != null ? Number(row.documentType) : 0,
    documentTip: row.documentTip != null ? Number(row.documentTip) : 0,
    seriesNo: row.seriesNo || '',
    designId: row.designId || '',
    isActive: row.isActive !== false ? 'true' : 'false',
    lastNumber: row.lastNumber != null ? String(row.lastNumber) : '0',
  };
}

export function DocumentSeriesDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete, canCopy } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Seri' : 'Seri Detayı', 'Belge türü ve tipi bazında matbu seri tanımı.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [vendors, setVendors] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const getVendorLabel = useCallback((vid) => {
    const v = vendors.find((item) => item.id === vid);
    if (!v) return '';
    return v.name ? `${v.textId || ''} - ${v.name}`.trim().replace(/^- /, '') : v.textId || '';
  }, [vendors]);

  // Lookup'lar (Distribütör + Dizayn) — modaldakiyle birebir aynı uçlar.
  useEffect(() => {
    Promise.all([
      fetch('/api/vendors/Select', { headers: authHeaders() })
        .then((res) => res.ok ? res.json() : [])
        .then((data) => setVendors(unwrapLookupItems(data)))
        .catch(() => {}),
      fetch('/api/documenttemplates', { headers: authHeaders() })
        .then((res) => res.ok ? res.json() : [])
        .then((data) => setDesigns(unwrapLookupItems(data)))
        .catch(() => {}),
    ]);
  }, []);

  // Tek kayıt yükleme: GET-by-id YOK. Önce navigation state'ten gelen row;
  // yoksa listeyi çekip id ile bul (mevcut sayfa form'u satırdan kuruyordu).
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    const stateRow = location.state?.row;
    if (stateRow && String(stateRow.id) === String(id)) {
      setForm(rowToForm(stateRow));
      // Nav-state satırı (liste $select) audit alanlarını içermez → tam kaydı arka planda
      // (bloklamadan) çekip audit'i doldur.
      fetch(`${API_BASE}/GetById?id=${encodeURIComponent(id)}`, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => {
          const rec = body?.Data || body?.data || body;
          if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        })
        .catch(() => {});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/GetById?id=${encodeURIComponent(id)}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Seri alınamadı (${res.status})`);
      const data = await res.json();
      const found = data?.Data || data?.data || data;
      if (!found) throw new Error('Seri bulunamadı');
      setForm(rowToForm({
        distributorId: pick(found, 'distributorId', 'DistributorId') || '',
        year: pick(found, 'year', 'Year'),
        documentType: pick(found, 'documentType', 'DocumentType'),
        documentTip: pick(found, 'documentTip', 'DocumentTip'),
        seriesNo: pick(found, 'seriesNo', 'SeriesNo') || '',
        designId: pick(found, 'designId', 'DesignId') || '',
        isActive: pick(found, 'isActive', 'IsActive'),
        lastNumber: pick(found, 'lastNumber', 'LastNumber'),
      }));
      setAudit({ createdBy: pick(found, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(found, 'createdDate', 'CreatedDate'), updatedBy: pick(found, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(found, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Seri alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, location.state]);

  useEffect(() => { load(); }, [load]);

  // Klonla: /document-series/new'e kopya veriyle gelindiyse formu doldur.
  // Kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar. location.key her navigasyonda değişir.
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildPayload = () => ({
    distributorId: form.distributorId,
    year: toInt(form.year),
    documentType: Number(form.documentType),
    documentTip: Number(form.documentTip),
    seriesNo: form.seriesNo.trim(),
    designId: form.designId || null,
    isActive: form.isActive === 'true',
    lastNumber: Math.max(0, toInt(form.lastNumber) || 0),
  });

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan
  // (önceki seri backend'e kaydedilmiş olur). Sadece yeni kayıt modunda anlamlı.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.distributorId) { showToast('error', 'Distribütör seçimi zorunludur'); return; }
    if (!form.seriesNo.trim()) { showToast('error', 'Seri No boş olamaz'); return; }
    const yearNum = toInt(form.year);
    if (!yearNum || yearNum < 2000 || yearNum > 2100) { showToast('error', 'Geçerli bir yıl giriniz'); return; }

    setSaving(true);
    try {
      const payload = buildPayload();
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(isNew ? payload : { id, ...payload }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || (isNew ? 'Kayıt eklenemedi' : 'Kayıt güncellenemedi'));
      if (isNew) {
        if (andNew) {
          showToast('success', 'Seri eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Seri başarıyla eklendi');
        setTimeout(() => navigate('/document-series'), 400);
        return;
      }
      showToast('success', 'Seri başarıyla güncellendi');
      setTimeout(() => navigate('/document-series'), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın tüm form bilgilerini taşıyarak yeni-kayıt ekranını aç.
  // Hiçbir şey kaydedilmez; kullanıcı "Kaydet" diyene kadar yeni seri oluşmaz.
  const handleClone = () => {
    navigate('/document-series/new', { state: { clone: { ...form } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || 'Kayıt silinemedi');
      showToast('success', 'Seri başarıyla silindi');
      setTimeout(() => navigate('/document-series'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme işlemi sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  // Talep 834 — yalnız seçili distribütörle EŞLEŞEN dizaynlar: o distribütöre ait olanlar +
  // VendorId'si boş olan GENEL dizaynlar (genel dizayn her distribütör için geçerlidir, elenmemeli).
  // Distribütör henüz seçilmemişken daraltacak bir kapsam yok → hepsi listelenir.
  // Kayıtlı dizayn süzgece takılırsa (dizaynın distribütörü sonradan değişmişse) yine de listede
  // durur — sessizce düşürmek, kullanıcının Kaydet'te dizaynı farkında olmadan kaybetmesi olurdu.
  const designOptions = useMemo(() => {
    const dist = form.distributorId;
    const matches = (d) => {
      const v = d.vendorId ?? d.VendorId ?? '';
      return !dist || !v || String(v) === String(dist);
    };
    const opts = designs.filter(matches).map((d) => ({ value: d.id, label: d.name || d.id }));
    const cur = form.designId;
    if (cur && !opts.some((o) => String(o.value) === String(cur))) {
      const kept = designs.find((d) => String(d.id) === String(cur));
      opts.push({ value: cur, label: `${kept?.name || cur} (farklı distribütör)` });
    }
    return [{ value: '', label: 'Seçiniz' }, ...opts];
  }, [designs, form.distributorId, form.designId]);

  const title = isNew ? 'Yeni Seri' : (form.seriesNo || 'Seri Detayı');
  const statusBadge = !isNew && (form.isActive === 'true'
    ? <StatusBadge tone="ok">Aktif</StatusBadge>
    : <StatusBadge tone="neutral">Pasif</StatusBadge>);

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/document-series')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni seri aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Seri Yönetimi', to: '/document-series' }, { label: isNew ? 'Yeni Seri' : 'Seri Detayı', current: true }]}
      status={statusBadge}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'DocumentSeries', entityId: id }}
      actions={actions}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="layers" title="Seri Bilgileri" subtitle="Distribütör, belge türü/tipi ve matbu seri tanımı">
          <Grid cols={2}>
            <FieldCustom label="Distribütör" req invalid={tried && !form.distributorId}>
              <RelationPicker
                config={RELATION_CONFIGS.vendor}
                rows={vendors}
                value={form.distributorId}
                selectedLabel={form.distributorId ? getVendorLabel(form.distributorId) : ''}
                onChange={(val) => set('distributorId', val)}
                placeholder="Seçiniz"
              />
            </FieldCustom>
            <FieldText form={form} set={set} tried={tried} k="year" label="Yıl" req num type="number" />
            <FieldSelect form={form} set={set} k="documentType" label="Belge Türü" options={DOCUMENT_TYPE_OPTIONS} req />
            <FieldSelect form={form} set={set} k="documentTip" label="Belge Tipi" options={DOCUMENT_TIP_OPTIONS} req />
            <FieldText form={form} set={set} tried={tried} k="seriesNo" label="Seri No" req mono />
            <FieldSelect form={form} set={set} k="designId" label="Dizayn" options={designOptions} />
            <FieldSelect form={form} set={set} k="isActive" label="Durum" options={ACTIVE_OPTIONS} />
          </Grid>
        </Section>

        <Section icon="list" title="Matbu No Sayacı" subtitle="En son yazdırılan matbu sıra no — düzenlenebilir; bir sonraki belge +1 alır"
          note="Mükerrer matbu no'ya izin verilmez (iptal edilen belgeler hariç). Sayaç geri alınırsa, çakışan numaralar baskı sırasında otomatik atlanır.">
          <Grid cols={2}>
            <FieldText form={form} set={set} k="lastNumber" label="Son Seri No (sayaç)" num type="number" />
            <FieldCustom label="Sıradaki Matbu No (önizleme)">
              <div className="dps-ff-input">
                <input
                  type="text"
                  readOnly
                  value={formatMatbuNo(form.seriesNo, toInt(form.year) || currentYear, (toInt(form.lastNumber) || 0) + 1)}
                />
              </div>
            </FieldCustom>
          </Grid>
        </Section>
      </div>

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.seriesNo}”</strong> serisini silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default DocumentSeriesDetailPage;
